export const FONT_PRESETS = {
    inter:     { display: 'Inter',              body: 'Inter',    label: 'Inter' },
    editorial: { display: 'Cormorant Garamond', body: 'DM Sans',  label: 'Space Mono' },
    tech:      { display: 'Syne',               body: 'Inter',    label: 'JetBrains Mono' },
    warm:      { display: 'Lora',               body: 'DM Sans',  label: 'DM Sans' },
    bold:      { display: 'Space Grotesk',      body: 'DM Sans',  label: 'Space Mono' },
    classic:   { display: 'Libre Baskerville',  body: 'Inter',    label: 'Inter' },
    modern:    { display: 'Plus Jakarta Sans',  body: 'DM Sans',  label: 'Space Mono' },
    elegant:   { display: 'Playfair Display',   body: 'Raleway',  label: 'Space Mono' },
    // --- Track D: presets por sector (mascotas/tech-ciber/deporte/oficios/artesanía/lujo) ---
    playful:    { display: 'Baloo 2',           body: 'Quicksand',           label: 'Quicksand' },
    cyber:      { display: 'Chakra Petch',      body: 'Inter',               label: 'JetBrains Mono' },
    sport:      { display: 'Anton',             body: 'Inter',               label: 'Archivo' },
    industrial: { display: 'Oswald',            body: 'Inter',               label: 'Bebas Neue' },
    artisan:    { display: 'Fraunces',          body: 'Bricolage Grotesque', label: 'Bricolage Grotesque' },
    luxe:       { display: 'Marcellus',         body: 'Cormorant Garamond',  label: 'Marcellus' },
};

// --- Lista ordenada para selectores de UI (Estudio de Tema) ---
// DRY: los 14 mismos presets que FONT_PRESETS, con etiqueta legible + fuente de
// titular (`display`) para previsualizar cada opción con su propia tipografía.
// Un preset nuevo solo se añade a FONT_PRESETS/FONT_URLS y a esta lista.
export const FONT_PRESET_LIST = [
    { id: 'inter',      name: 'Inter (por defecto)' },
    { id: 'modern',     name: 'Moderno' },
    { id: 'editorial',  name: 'Editorial' },
    { id: 'classic',    name: 'Clásico' },
    { id: 'elegant',    name: 'Elegante' },
    { id: 'warm',       name: 'Cálido' },
    { id: 'bold',       name: 'Impactante' },
    { id: 'tech',       name: 'Tecnológico' },
    { id: 'cyber',      name: 'Cyber' },
    { id: 'playful',    name: 'Divertido' },
    { id: 'sport',      name: 'Deportivo' },
    { id: 'industrial', name: 'Industrial' },
    { id: 'artisan',    name: 'Artesanal' },
    { id: 'luxe',       name: 'Lujo' },
].map(f => ({ ...f, display: (FONT_PRESETS[f.id] || FONT_PRESETS.inter).display }));

/* ============================================================================
 * LA FUENTE ES DEL SITIO, NO DE CADA BLOQUE
 * ============================================================================
 * `fontFamily` nació como prop de bloque y se replica en todos (166 en la BD local, 14 sin él,
 * un sitio mezclando 'editorial' con 'elegant'), pese a que la invariante nº9 dice que debe ser
 * el MISMO en todo el sitio. Peor: cada superficie elegía por su cuenta — `render.js` leía el
 * hero de la página PINTADA y `compileSiteHtml` el de la HOME → el editor y el sitio publicado
 * podían salir con tipografías distintas.
 *
 * La verdad pasa a `siteData.site_tokens.fontFamily` (lo escribe el saneador del backend
 * `llm_sanitizers._fix_font_unity` al generar y el Estudio de Tema al cambiarla), con EL PROP DE
 * BLOQUE COMO FALLBACK: un sitio ya guardado (sin token) resuelve exactamente la misma fuente que
 * antes → sale byte-idéntico.
 *
 * PUNTO DE CONTACTO con el render: `render.js` sigue llamando a `loadFontsForPreset(...)` y
 * `getFontVariables(...)` con el prop del hero de la página, como siempre; si hay una fuente de
 * SITIO fijada (`fijarFuenteSitio`, que llama `store` cada vez que cambia `siteData`), esa GANA
 * aquí dentro. Así el editor y el publicado resuelven por el mismo sitio sin tocar render.js.
 * Es el mismo patrón de estado de render que `setRenderLang` (utils/i18n.js).
 */
const _FUENTE_DEFAULT = 'inter';
// Nombre de fuente "suelto" admisible (retrocompat: sitios legacy con `fontFamily: 'Sora'`, que
// getFontVariables interpola tal cual). Deja fuera comillas, `<`, `;`, `}`… → el valor viaja a un
// `<style>` del HTML publicado (compileSiteHtml) y no puede romper el elemento.
const _NOMBRE_FUENTE_OK = /^[\w][\w .+-]{0,39}$/;

let _fuenteSitio = '';   // '' = sin fuente de sitio → manda lo que pase el llamante (retrocompat)

/** Normaliza un id/nombre de fuente a algo seguro de interpolar, o '' si no lo es. */
function _fuenteSegura(v) {
    if (typeof v !== 'string') return '';
    const slug = v.toLowerCase().replace(/['"]/g, '').trim();
    if (FONT_PRESETS[slug]) return slug;
    const crudo = v.replace(/['"]/g, '').trim();
    return _NOMBRE_FUENTE_OK.test(crudo) ? crudo : '';
}

/**
 * Fuente de TODO el sitio (función PURA, sin DOM ni estado).
 * Orden: `site_tokens.fontFamily` (la verdad) → `fontFamily` del hero de la home (o del 1er
 * bloque que lo traiga: el fallback retrocompatible) → 'inter'.
 * @param {object} siteData
 * @returns {string} id de preset (o nombre suelto legacy) listo para getFontVariables/FONT_URLS.
 */
export function resolverFuenteSitio(siteData) {
    if (!siteData || typeof siteData !== 'object') return _FUENTE_DEFAULT;
    const token = _fuenteSegura((siteData.site_tokens || {}).fontFamily);
    if (token) return token;

    const pages = Array.isArray(siteData.pages) ? siteData.pages : [];
    const home = pages.find(p => p && p.id === 'home') || pages[0] || null;
    const bloques = (home && Array.isArray(home.blocks)) ? home.blocks : [];
    const hero = bloques.find(b => b && b.type === 'hero') || bloques[0] || null;
    const delHero = _fuenteSegura(hero && hero.props && hero.props.fontFamily);
    if (delHero) return delHero;

    // Último recurso: el primer bloque de todo el sitio que traiga una fuente utilizable (cubre
    // los sitios cuyo hero es justo uno de los bloques a los que les falta el prop).
    for (const p of pages) {
        for (const b of (p && Array.isArray(p.blocks) ? p.blocks : [])) {
            const f = _fuenteSegura(b && b.props && b.props.fontFamily);
            if (f) return f;
        }
    }
    return _FUENTE_DEFAULT;
}

/** Fija la fuente de sitio para los siguientes renders (la llama store.js al cambiar siteData). */
export function fijarFuenteSitio(id) {
    _fuenteSitio = _fuenteSegura(id);
}

/** Fuente de sitio vigente ('' si no hay ninguna fijada). */
export function fuenteSitioActual() {
    return _fuenteSitio;
}

/* ── CIERRE DE LA PILA DE FUENTES ─────────────────────────────────────────────────────────
 * Las variables salían con la familia DESNUDA (`--font-display: "Marcellus"`), sin genérico
 * detrás. Con una pila de un solo nombre, cuando falta un glifo —o la webfont de Google aún no
 * ha llegado— el navegador no tiene a qué caer DENTRO de la pila y se va a su fuente de último
 * recurso: en Linux eso pinta la `ñ` como caja de tofu con el código dentro y la `é`/`Á` como
 * un hueco en blanco. Es decir, el texto estaba perfecto (verificado en BD: U+00F1) y aun así
 * se veía roto.
 * Cerrar con un genérico + caras del sistema que SÍ traen latín acentuado lo evita, y de paso
 * da un respaldo decente mientras la webfont carga.
 * Los valores son literales nuestros; las familias vienen de FONT_PRESETS o de un nombre legacy
 * ya validado por `_fuenteSegura` (su regex excluye comillas, `;` y `}`), así que esto sigue
 * siendo seguro de interpolar en el <style> del HTML publicado. */
const _GEN_SERIF = '"Iowan Old Style", Georgia, "Times New Roman", serif';
const _GEN_SANS  = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const _GEN_MONO  = 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

const _FAM_SERIF = new Set(['Cormorant Garamond', 'Lora', 'Libre Baskerville',
                            'Playfair Display', 'Marcellus', 'Fraunces']);
const _FAM_MONO  = new Set(['Space Mono', 'JetBrains Mono']);

/** `Familia` → `"Familia", <genérico acorde>`. Sin coincidencia cae a sans (el caso legacy). */
function _pilaFuente(familia) {
    const gen = _FAM_MONO.has(familia) ? _GEN_MONO
              : _FAM_SERIF.has(familia) ? _GEN_SERIF
              : _GEN_SANS;
    return `"${familia}", ${gen}`;
}

/* ── FUENTE DE ETIQUETAS Y BOTONES (site_tokens.labelFont) ─────────────────────────────────
 * La tercera fuente del preset (`label`) la pintan los botones, la tabla comparativa, los
 * antetítulos y la capa de rótulo de site.css, y antes solo se cambiaba con el preset entero (el
 * dueño la vio «hardcodeada»: Bebas Neue en `industrial`). El Estudio de Tema la deja ser la del
 * estilo, la del texto o la de los títulos. Ausente o con cualquier otro valor = 'estilo': un sitio
 * que no la toca pinta lo de siempre. El valor solo ELIGE entre las tres familias del preset: nunca
 * se interpola, así que lo que traiga el JSON guardado no llega al <style> del publicado. */
export const MODOS_ETIQUETAS = ['estilo', 'texto', 'titulos'];

/** Modo de la fuente de etiquetas ('estilo' si el valor no es uno de los tres). */
export function modoEtiquetas(v) {
    return MODOS_ETIQUETAS.indexOf(v) >= 0 ? v : 'estilo';
}

/**
 * Variables CSS de tipografía (--font-display/body/label).
 * OJO: si hay fuente de SITIO fijada (`fijarFuenteSitio`), GANA sobre `presetId` — es lo que
 * mantiene una sola familia en todo el sitio sin que cada llamante tenga que resolverla (ver la
 * cabecera "LA FUENTE ES DEL SITIO"). Para previsualizar una fuente suelta usa FONT_PRESETS.
 * `etiquetas` = `site_tokens.labelFont` (ver MODOS_ETIQUETAS); lo pasan render.js y compileSiteHtml.
 */
export const getFontVariables = (presetId, etiquetas) => {
    if (_fuenteSitio) presetId = _fuenteSitio;
    if (!presetId) presetId = _FUENTE_DEFAULT;
    const normalizedId = presetId.toLowerCase().replace(/['"]/g, '').trim();

    // Si existe en presets, usarlo
    const delPreset = variablesDePreset(normalizedId, etiquetas);
    if (delPreset) return delPreset;

    // Fallback dinámico: si el LLM tira "Inter" o "Sora" directo en legacy
    return {
        '--font-display': _pilaFuente(presetId),
        '--font-body': _pilaFuente(presetId),
        '--font-label': _pilaFuente(presetId)
    };
};

/** ¿Es `id` uno de los presets del catálogo? (Propio: `constructor` o `__proto__` no lo son.) */
export function esPresetDeFuente(id) {
    return typeof id === 'string' && Object.prototype.hasOwnProperty.call(FONT_PRESETS, id);
}

/**
 * Variables de tipografía de UN preset del catálogo, SIN la fuente de sitio (que `getFontVariables`
 * impone). Las usa «Texto de la sección» (F4, utils/texto-seccion.js), que cambia la fuente de una sola
 * sección. Solo ids de FONT_PRESETS (nunca un nombre suelto): con otra cosa, null.
 */
export function variablesDePreset(presetId, etiquetas) {
    if (!esPresetDeFuente(presetId)) return null;
    const preset = FONT_PRESETS[presetId];
    const modo = modoEtiquetas(etiquetas);
    const label = modo === 'texto' ? preset.body : modo === 'titulos' ? preset.display : preset.label;
    return {
        '--font-display': _pilaFuente(preset.display),
        '--font-body': _pilaFuente(preset.body),
        '--font-label': _pilaFuente(label)
    };
}

// --- 3.4 FONT LOADER DINÁMICO ---
// Solo Inter se carga en base.html. Las demás se cargan bajo demanda aquí.
export const FONT_URLS = {
    // Inter TAMBIÉN se declara: `base.html` es el del EDITOR; el sitio PUBLICADO (page_detail.html)
    // solo emite los <link> de aquí, así que con '' publicaba sin ninguna webfont y el cuerpo caía a
    // system-ui (auditoría de producción, 18-09-2026). En el editor cargarla dos veces es inocuo.
    inter:     'Inter:wght@300;400;500;600;700;800;900',
    editorial: 'Cormorant+Garamond:wght@300;400;700&family=DM+Sans:wght@300;400;700;800&family=Space+Mono:wght@400;700',
    tech:      'Syne:wght@400;700;800&family=JetBrains+Mono:wght@300;400;700;800&family=Inter:wght@300;400;500;600;700;800;900',
    warm:      'Lora:wght@400;500;600;700&family=DM+Sans:wght@300;400;700;800',
    bold:      'Space+Grotesk:wght@300;400;700&family=DM+Sans:wght@300;400;700;800&family=Space+Mono:wght@400;700',
    classic:   'Libre+Baskerville:wght@400;700&family=Inter:wght@300;400;500;600;700;800;900',
    modern:    'Plus+Jakarta+Sans:wght@300;400;700;800&family=DM+Sans:wght@300;400;700;800&family=Space+Mono:wght@400;700',
    elegant:   'Playfair+Display:wght@400;500;600;700;800;900&family=Raleway:wght@300;400;700;800;900&family=Space+Mono:wght@400;700',
    // Track D — los presets con Inter en body/label la llevan en su URL: el publicado no tiene base.html.
    playful:    'Baloo+2:wght@400;500;600;700;800&family=Quicksand:wght@300;400;500;600;700',
    cyber:      'Chakra+Petch:wght@400;500;600;700&family=JetBrains+Mono:wght@300;400;700;800&family=Inter:wght@300;400;500;600;700;800;900',
    sport:      'Anton&family=Archivo:wght@400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700;800;900',
    industrial: 'Oswald:wght@300;400;500;600;700&family=Bebas+Neue&family=Inter:wght@300;400;500;600;700;800;900',
    artisan:    'Fraunces:wght@400;500;600;700;800;900&family=Bricolage+Grotesque:wght@400;500;600;700;800',
    luxe:       'Marcellus&family=Cormorant+Garamond:wght@300;400;500;600;700',
};

const _loadedFonts = new Set(['inter']);

/**
 * Carga las fuentes necesarias para un preset dado.
 * No-op si ya están cargadas o si el preset es 'inter'.
 * MISMA regla que `getFontVariables` (y tiene que serlo: si aquí se cargara la fuente del hero y
 * allí se pintara la del sitio, el texto saldría con la fuente sin descargar). Quien cambia la
 * fuente del sitio debe FIJARLA ANTES de llamar aquí (el Estudio de Tema escribe el token primero).
 */
export function loadFontsForPreset(presetId) {
    if (_fuenteSitio) presetId = _fuenteSitio;
    cargarFuentesDePreset((presetId || _FUENTE_DEFAULT).toLowerCase().replace(/['"]/g, '').trim());
}

/** Carga las familias de UN preset tal cual, sin la fuente de sitio (la de una sección, F4). */
export function cargarFuentesDePreset(id) {
    if (typeof document === 'undefined' || _loadedFonts.has(id)) return;
    const families = Object.prototype.hasOwnProperty.call(FONT_URLS, id) ? FONT_URLS[id] : '';
    if (!families) return; // Preset desconocido o inter

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
    document.head.appendChild(link);
    _loadedFonts.add(id);
}

/**
 * Las hojas de TODOS los presets, para los desplegables de fuente (decisión del dueño del 19-10-2026, D-1193:
 * «cada estilo con su propia letra»). Son las mismas hojas que `cargarFuentesDePreset` (cada una, una vez), así
 * que no hay dos declaraciones distintas de una familia, y el navegador solo baja los ficheros de las letras
 * que se pintan: los nombres de la lista. Se llama al ABRIR el desplegable, nunca al cargar el editor.
 */
export function cargarMuestrasDeFuentes() {
    FONT_PRESET_LIST.forEach((f) => cargarFuentesDePreset(f.id));
}

/** Descarga una hoja de Google Fonts por su URL una sola vez (la itálica de la palabra en cursiva, F6). */
export function cargarHojaDeFuente(url) {
    if (typeof document === 'undefined' || !url || _loadedFonts.has(url)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
    _loadedFonts.add(url);
}
